<template>
  <div class="field" :class="{ disabled: isDisabled }">
    <div id="triggers">
      <div class="ui cards">
        <trigger
          v-for="(t, index) in externalTriggers"
          :key="'e' + index"
          :initTrigger="t"
          :componentNamePrefix="t.externalType"
          :triggerIndex="index"
          @change="onTriggerChange(t, $event, 'external', index)"
          @delete="deleteTrigger('external', index)"
          @validityUpdated="updateTriggerValidity('external', index, $event)"
        ></trigger>
        <trigger
          v-for="(t, index) in lifecycleTriggers"
          :key="'l' + index"
          :initTrigger="t"
          :componentNamePrefix="'lifecycle'"
          :triggerIndex="index"
          @change="onTriggerChange(t, $event, 'lifecycle', index)"
          @delete="deleteTrigger('lifecycle', index)"
          @validityUpdated="updateTriggerValidity('lifecycle', index, $event)"
        ></trigger>
        <trigger
          v-for="(t, index) in contextTriggers"
          :key="'c' + index"
          :initTrigger="t"
          :componentNamePrefix="'context'"
          :triggerIndex="index"
          @change="onTriggerChange(t, $event, 'context', index)"
          @delete="deleteTrigger('context', index)"
          @validityUpdated="updateTriggerValidity('context', index, $event)"
        ></trigger>
        <trigger
          v-for="(t, index) in serviceTriggers"
          :key="'s' + index"
          :initTrigger="t"
          :componentNamePrefix="t.serviceType"
          :triggerIndex="index"
          @change="onTriggerChange(t, $event, 'services', index)"
          @delete="deleteTrigger('services', index)"
          @validityUpdated="updateTriggerValidity('services', index, $event)"
        ></trigger>
        <div class="card">
          <div class="content">
            <div class="header">
              {{ $t('NEW', { thing: $tc('TRIGGER', 1) }) }}
              <i class="right floated icon"></i>
            </div>
            <div class="meta">{{ $t('TRIGGER_META') }}</div>
          </div>
          <div class="content">
            <h4>{{ $tc('EXTERNAL_TRIGGER', 2) }}</h4>
            <div class="description">
              <div class="ui horizontal list">
                <a class="item" @click.prevent="addCronTrigger">{{ $t('CRON') | capitalize }}</a>
                <a class="item" @click.prevent="addHotkeyTrigger">{{
                  $t('HOTKEY') | capitalize
                }}</a>
                <a class="item" @click.prevent="addWindowTrigger">{{
                  $t('WINDOW') | capitalize
                }}</a>
                <a class="item" @click.prevent="addTableTrigger">{{
                  $tc('TABLE', 1) | capitalize
                }}</a>
                <a class="item" @click.prevent="addKeyboardTrigger">{{
                  $t('KEYBOARD') | capitalize
                }}</a>
                <a class="item" @click.prevent="addFileSystemTrigger">{{
                  $t('FILESYSTEM') | capitalize
                }}</a>
                <a class="item" @click.prevent="addEventInterceptTrigger">
                  {{ $t('INTERCEPT_EVENT') }}
                </a>
              </div>
            </div>
          </div>
          <div class="content">
            <h4>{{ $tc('CONTEXT_TRIGGER', 2) }}</h4>
            <div class="description">
              <div class="ui horizontal list">
                <a class="item" @click.prevent="addContextChangeTrigger">
                  {{ $t('CONTEXT_TRIGGER_CHANGE') }}
                </a>
              </div>
            </div>
          </div>
          <div class="content">
            <h4>{{ $tc('LIFECYCLE_TRIGGER', 2) }}</h4>
            <div class="description">
              <div class="ui horizontal list">
                <a class="item" @click.prevent="addAttachedTrigger">
                  {{ $t('LIFECYCLE_TRIGGER_ATTACHED') }}
                </a>
                <a class="item" @click.prevent="addDetachedTrigger">
                  {{ $t('LIFECYCLE_TRIGGER_DETACHED') }}
                </a>
                <a class="item" @click.prevent="addManateeStartedTrigger">
                  {{ $t('LIFECYCLE_TRIGGER_MANATEESTARTED') }}
                </a>
                <a class="item" @click.prevent="addNavigatedTrigger">
                  {{ $t('LIFECYCLE_TRIGGER_NAVIGATED') }}
                </a>
              </div>
            </div>
          </div>
          <div class="content">
            <h4>{{ $tc('SERVICE_TRIGGER', 2) }}</h4>
            <div class="description">
              <div class="ui horizontal list">
                <a
                  class="item"
                  v-for="t in serviceTypes.filter(t => t.canTrigger)"
                  :key="t.name"
                  :title="$t(t.name + '_SERVICE_TRIGGER')"
                  @click.prevent="addServiceTrigger(t.name)"
                >
                  <i class="icon" :class="t.icon"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@charset "utf-8";
@import '../../../scss/variables';
</style>

<script>
// @flow
import _ from 'lodash'
import { Service } from 'services'
import Trigger from './Triggers/Trigger'
import { mapGetters, mapMutations } from 'vuex'
import Conf from '../../../services/conf'

export default {
  name: 'flow-triggers',

  components: { Trigger },

  props: {
    isDisabled: {
      type: Boolean,
      default: () => false
    }
  },

  computed: {
    triggers() {
      return this.$store.state.flowEditor.flow.typeConfig.triggers
    },
    flowType() {
      return this.$store.state.flowEditor.flow.type
    },
    externalTriggers() {
      return (this.triggers && this.triggers.external) || []
    },

    lifecycleTriggers() {
      return (this.triggers && this.triggers.lifecycle) || []
    },

    contextTriggers() {
      return (this.triggers && this.triggers.context) || []
    },

    serviceTriggers() {
      return (this.triggers && this.triggers.services) || []
    },

    serviceTypes() {
      return Service.serviceTypes()
    }
  },

  methods: {
    ...mapMutations('flowEditor', [
      'deleteFlowTrigger',
      'addFlowTrigger',
      'updateFlowTrigger',
      'updateIsTriggerValid'
    ]),
    deleteTrigger(type, idx) {
      this.deleteFlowTrigger({ triggerType: type, idx })
    },
    updateTriggerValidity(type, idx, isValid) {
      this.updateIsTriggerValid({ triggerType: type, triggerIdx: idx, isValid: isValid })
    },

    onTriggerChange(trigger, newTrigger, triggerType, index) {
      this.updateFlowTrigger({
        newTrigger: { ...trigger, ...newTrigger },
        triggerType: triggerType,
        triggerIdx: index
      })
    },

    addServiceTrigger(type) {
      this.addFlowTrigger({
        triggerType: 'services',
        trigger: {
          serviceType: type,
          configuration: {}
        }
      })
    },

    addTableTrigger() {
      this.addFlowTrigger({
        triggerType: 'external',
        trigger: {
          externalType: 'TABLE',
          configuration: { tableId: '' }
        }
      })
    },

    addCronTrigger() {
      this.addFlowTrigger({
        triggerType: 'external',
        trigger: {
          externalType: 'CRON',
          configuration: { cron: '* * * * *' }
        }
      })
    },

    addFileSystemTrigger() {
      this.addFlowTrigger({
        triggerType: 'external',
        trigger: {
          externalType: 'FILESYSTEM',
          configuration: { directory: '', match: '' }
        }
      })
    },

    addEventInterceptTrigger() {
      this.addFlowTrigger({
        triggerType: 'external',
        trigger: {
          externalType: 'INTERCEPT',
          configuration: { eventType: '', fieldId: '' }
        }
      })
    },

    addKeyboardTrigger() {
      this.addFlowTrigger({
        triggerType: 'external',
        trigger: {
          externalType: 'KEYBOARD',
          configuration: {
            match: '.*',
            lookbackMs: 300,
            debounceMs: 300,
            mode: Conf.disallowKeyboardTriggerDebounce ? 'lookback' : 'debounce'
          }
        }
      })
    },

    addHotkeyTrigger() {
      this.addFlowTrigger({
        triggerType: 'external',
        trigger: {
          externalType: 'HOTKEY',
          configuration: { keys: [], modifiers: [] }
        }
      })
    },

    addWindowTrigger() {
      this.addFlowTrigger({
        triggerType: 'external',
        trigger: {
          externalType: 'WINDOW_SHOWN',
          configuration: {}
        }
      })
    },

    addContextChangeTrigger() {
      this.addFlowTrigger({
        triggerType: 'context',
        trigger: {
          subject: '',
          regexpOld: '',
          regexpNew: ''
        }
      })
    },

    addAttachedTrigger() {
      this.addFlowTrigger({
        triggerType: 'lifecycle',
        trigger: { event: 'ATTACHED' }
      })
    },

    addDetachedTrigger() {
      this.addFlowTrigger({
        triggerType: 'lifecycle',
        trigger: { event: 'DETACHED' }
      })
    },
    addManateeStartedTrigger() {
      this.addFlowTrigger({
        triggerType: 'lifecycle',
        trigger: { event: 'MANATEESTARTED' }
      })
    },

    addNavigatedTrigger() {
      this.addFlowTrigger({
        triggerType: 'lifecycle',
        trigger: { event: 'NAVIGATED' }
      })
    }
  }
}
</script>
